import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "use-debounce";
import rootStores from "../../stores";
import DataTable from "../../components/table/Table";
import "./ConcertHeader.css";
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Checkbox
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import { dataColumns, SendStatus } from "./consts";
import { updateCostumersData } from "../../fetchers/concert";
import TicketMessageDialog, { TicketMessageAction, TicketMessageChannel } from "./TicketMessageDialog";
import DateRangePopover from "./DateRangePopover";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { CircularProgress } from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import RefreshIcon from "@mui/icons-material/Refresh";

const concertStore = rootStores.ConcertStore;
const navBarStore = rootStores.NavBarStore;
const REFRESH_COOLDOWN = 15;

const deepClone = <T,>(obj: T): T => JSON.parse(JSON.stringify(obj));

const EDITABLE_FIELDS = ["firstName", "lastName", "phoneNumber"] as const;
type EditableField = (typeof EDITABLE_FIELDS)[number];
type TicketRow = any;

function getCostumerNumberFromRow(row: TicketRow): string {
  return String(row?.customer?.costumerNumber ?? row?.costumerNumber ?? "").trim();
}

function getCustomerField(row: TicketRow, field: EditableField): string {
  return String(row?.customer?.[field] ?? "");
}

function setCustomerField(row: TicketRow, field: EditableField, value: string): TicketRow {
  return {
    ...row,
    customer: {
      ...(row.customer || {}),
      [field]: value,
      costumerNumber: row.customer?.costumerNumber ?? row.costumerNumber
    }
  };
}

// ===== phone validation (used ONLY on save/send) =====
function normalizePhone(raw: string): string {
  const v = String(raw ?? "").trim();
  if (!v) return "";
  const plus = v.startsWith("+") ? "+" : "";
  const digits = v.replace(/\D/g, "");
  return plus ? `+${digits}` : digits;
}

function isValidILPhone(raw: string): boolean {
  const n = normalizePhone(raw);
  if (!n) return false;

  if (n.startsWith("0")) return /^\d{10}$/.test(n);

  const digits = n.startsWith("+") ? n.slice(1) : n;
  if (digits.startsWith("972")) return /^972\d{9}$/.test(digits);

  return false;
}

function phoneErrorText(raw: string): string {
  if (!String(raw ?? "").trim()) return "Phone number is required";
  if (!isValidILPhone(raw)) return "Invalid phone. Use 10 digits (05XXXXXXXX) or +972XXXXXXXXX";
  return "";
}

// ===== selection validation =====
function getOrderIdSales(row: TicketRow): string {
  return String(row?.orderIdSales ?? "").trim();
}

function validateSameOrderIdSales(rows: TicketRow[]) {
  if (!rows.length) return { ok: false as const, message: "No rows selected" };
  const first = getOrderIdSales(rows[0]);
  if (!first) return { ok: false as const, message: "Selected rows are missing orderIdSales" };
  const allSame = rows.every(r => getOrderIdSales(r) === first);
  if (!allSame) return { ok: false as const, message: "All selected rows must have the same orderIdSales" };
  return { ok: true as const };
}

function getClosestEvent(events: any[]) {
  if (!events || events.length === 0) return null;

  const now = Date.now();
  const mapped = events.map((e: any) => ({ ...e, time: new Date(e.eventDate).getTime() })).filter((e: any) => !isNaN(e.time));

  const futureEvents = mapped.filter((e: any) => e.time >= now).sort((a: any, b: any) => a.time - b.time);
  if (futureEvents.length > 0) return futureEvents[0];

  return mapped.sort((a: any, b: any) => b.time - a.time)[0] || null;
}

const ConcertTickets: React.FC = observer(() => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const [pageSize, setPageSize] = useState<number>(100);
  const [page, setPage] = useState(0);

  const [isEditing, setIsEditing] = useState(false);
  const [originalRows, setOriginalRows] = useState<TicketRow[]>([]);
  const [draftRows, setDraftRows] = useState<TicketRow[]>([]);

  const [filter, setFilter] = useState({ sendStatus: [] as string[], search: "" });
  const [debouncedSearch] = useDebounce(filter.search, 500);
  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLElement | null>(null);
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({ from: "", to: "" });
  const isDatePopoverOpen = Boolean(dateAnchorEl);
  const [missingOnly, setMissingOnly] = useState(false);
  const { events, event, tickets, setEvent } = concertStore;

  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageDialogAction, setMessageDialogAction] = useState<TicketMessageAction>("send");
  const [messageDialogRow, setMessageDialogRow] = useState<TicketRow | null>(null);
  const [refreshCooldown, setRefreshCooldown] = useState(0);

  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [dialogPhoneError, setDialogPhoneError] = useState<string>("");

  // ONLY used to show errors in edit mode (after SAVE attempt)
  const [phoneErrorsByRowId, setPhoneErrorsByRowId] = useState<Record<string, string>>({});

  const [isApplyingDateRange, setIsApplyingDateRange] = useState(false);

  useEffect(() => {
    concertStore.fetchEvents();
  }, []);

  useEffect(() => {
    if (refreshCooldown <= 0) return;

    const t = setInterval(() => {
      setRefreshCooldown((s) => s - 1);
    }, 1000);

    return () => clearInterval(t);
  }, [refreshCooldown]);

  useEffect(() => {
    const closest = getClosestEvent(events);
  
    if (closest) {
      if (closest.eventId !== event?.eventId) {
        setEvent(closest);
        setPage(0);
      }
    } else {
      setEvent(null);
      concertStore.setTickets([]);
      concertStore.setTotalRecordsInDB(0);
      concertStore.setTotalFilteredRecords(0);
    }
  }, [events]);

  useEffect(() => {
    if (!event?.eventId) return;
    concertStore.fetchTicketsByEventPaged(
      event.eventId,
      page,
      pageSize,
      filter.search,
      filter.sendStatus,
      missingOnly
    );
  }, [event, page, pageSize, filter.search, filter.sendStatus, missingOnly]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [event?.eventId, page, pageSize, debouncedSearch, filter.sendStatus, isEditing, missingOnly]);

  const handleRefresh = async () => {
    if (!event?.eventId) return;
    if (concertStore.isLoading) return;
    if (refreshCooldown > 0) return;

    setRefreshCooldown(REFRESH_COOLDOWN);

    await concertStore.fetchTicketsByEvaXMLPaged(
      event.eventId,
      page,
      pageSize,
      filter.search,
      filter.sendStatus,
      missingOnly
    );
  };

  const handleFilter = (searchValue?: string) => {
    if (typeof searchValue === "string") {
      setFilter(prev => ({ ...prev, search: searchValue }));
    }
    setPage(0); // ✅ effect will fetch once
  };

  const setSendStatus = (statuses: string[]) => {
    handleCancelClick();
    setFilter(prev => ({ ...prev, sendStatus: statuses }));
  };

  // const clearFilters = async () => {
  //   setFilter({ sendStatus: [], search: "" });
  //   setDateRange({ from: "", to: "" });
  //   await concertStore.fetchEvents();
  //   handleFilter();
  // };

  const clearFilters = async () => {
    handleCancelClick();

    setPage(0);
    setMissingOnly(false);

    setFilter({ sendStatus: [], search: "" });
    setDateRange({ from: "", to: "" });

    await concertStore.fetchEvents();
  };

  const onSearchFieldKeyDown = (ev: any) => {
    if (ev.key === "Enter") {
      handleFilter();
      ev.preventDefault();
    }
  };

  const openDatePopover = (e: React.MouseEvent<HTMLElement>) => setDateAnchorEl(e.currentTarget);
  const closeDatePopover = () => setDateAnchorEl(null);

  const applyDateRange = async () => {
    try {
      setIsApplyingDateRange(true);
      handleCancelClick();
      closeDatePopover();
  
      await concertStore.fetchEvents(dateRange.from, dateRange.to);
  
      const closest = getClosestEvent(concertStore.events);
  
      if (closest) {
        setEvent(closest);
        setPage(0);
      } else {
        setEvent(null);
        concertStore.setTickets([]);
        concertStore.setTotalRecordsInDB(0);
        concertStore.setTotalFilteredRecords(0);
      }
  
    } finally {
      setIsApplyingDateRange(false);
    }
  };

  const mappedRows = useMemo(
    () => tickets.map((row: any) => ({ ...row, id: row._id })),
    [tickets]
  );

  const rowsToRender = isEditing ? draftRows : mappedRows;

  const rowsById = useMemo(() => {
    const m = new Map<string, TicketRow>();
    for (const r of rowsToRender) m.set(String(r.id), r);
    return m;
  }, [rowsToRender]);

  const selectedRows = useMemo(() => {
    return Array.from(selectedIds)
      .map(id => rowsById.get(id))
      .filter(Boolean) as TicketRow[];
  }, [selectedIds, rowsById]);

  const originalByCustomerNumber = useMemo(() => {
    const m = new Map<string, TicketRow>();
    for (const r of originalRows) {
      const num = getCostumerNumberFromRow(r);
      if (num) m.set(num, r);
    }
    return m;
  }, [originalRows]);

  const updateDraftCustomerField = (rowId: any, field: EditableField, value: string) => {
    setDraftRows(prev =>
      prev.map(r => (r.id === rowId ? setCustomerField(r, field, value) : r))
    );

    if (field === "phoneNumber") {
      setPhoneErrorsByRowId(prev => {
        const next = { ...prev };
        delete next[String(rowId)];
        return next;
      });
    }
  };

  const openMessageDialog = (action: TicketMessageAction, row: TicketRow) => {
    setBulkMode(false); // ✅ single mode
    setMessageDialogAction(action);
    setMessageDialogRow(row);
    setMessageDialogOpen(true);
    setDialogPhoneError("");
  };

  const openBulkMessageDialog = () => {
    // validate same orderIdSales before opening
    const v = validateSameOrderIdSales(selectedRows);
    if (!v.ok) {
      setDialogPhoneError(v.message);
      // still open dialog so user sees error? you asked "check before sending" - we can just show in dialog:
      setBulkMode(true);
      setMessageDialogAction("send");
      setMessageDialogRow(null);
      setMessageDialogOpen(true);
      return;
    }

    setBulkMode(true);
    setMessageDialogAction("send");
    setMessageDialogRow(null);
    setMessageDialogOpen(true);
    setDialogPhoneError("");
  };

  const closeMessageDialog = () => {
    setMessageDialogOpen(false);
    setMessageDialogRow(null);
    setDialogPhoneError("");
    setBulkMode(false);
  };

  const columns = useMemo(() => {
    const allOnPageSelected =
      rowsToRender.length > 0 &&
      rowsToRender.every((r: any) => selectedIds.has(String(r.id)));
    const someOnPageSelected =
      rowsToRender.some((r: any) => selectedIds.has(String(r.id))) && !allOnPageSelected;

    const selectCol = {
      field: "__select",
      headerName: "",
      width: 52,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <Checkbox
          size="small"
          checked={allOnPageSelected}
          indeterminate={someOnPageSelected}
          disabled={isEditing || rowsToRender.length === 0}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            e.stopPropagation();
            setSelectedIds(prev => {
              const next = new Set(prev);
              if (allOnPageSelected) {
                rowsToRender.forEach((r: any) => next.delete(String(r.id)));
              } else {
                rowsToRender.forEach((r: any) => next.add(String(r.id)));
              }
              return next;
            });
          }}
        />
      ),
      renderCell: (params: any) => {
        const id = String(params.id);
        const checked = selectedIds.has(id);

        return (
          <Checkbox
            size="small"
            checked={checked}
            disabled={isEditing}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation();
              setSelectedIds(prev => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              });
            }}
          />
        );
      }
    };

    const base = dataColumns.map((col: any) => {
      if (!EDITABLE_FIELDS.includes(col.field)) return col;

      return {
        ...col,
        sortable: false,
        renderCell: (params: any) => {
          const row = params.row;
          const currentValue = getCustomerField(row, col.field);

          if (!isEditing) return currentValue;

          const isPhone = col.field === "phoneNumber";
          const rowId = String(params.id);
          const errMsg = isPhone ? phoneErrorsByRowId[rowId] : "";

          return (
            <TextField
              value={currentValue}
              onChange={(e) => updateDraftCustomerField(params.id, col.field, e.target.value)}
              size="small"
              variant="standard"
              fullWidth
              inputProps={{ style: { textAlign: "right" } }}
              onKeyDown={(e) => e.stopPropagation()}
              onKeyUp={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              autoComplete="off"
              error={Boolean(isPhone && errMsg)}
              helperText={isPhone && errMsg ? errMsg : " "}
            />
          );
        }
      };
    });

    const sendCol = {
      field: "__send",
      headerName: "Send",
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => {
        return (
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={isEditing}
            onClick={() => openMessageDialog("send", params.row)}
          >
            Send
          </Button>
        );
      }
    };

    return [selectCol, ...base, sendCol];
  }, [isEditing, phoneErrorsByRowId, selectedIds, rowsToRender]);

  const handleEditClick = () => {
    if (!mappedRows.length) return;
    const snapshot = deepClone(mappedRows);
    setOriginalRows(snapshot);
    setDraftRows(deepClone(snapshot));
    setIsEditing(true);
    setPhoneErrorsByRowId({});
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setDraftRows([]);
    setOriginalRows([]);
    setPhoneErrorsByRowId({});
  };

  const buildChangedCustomersPayload = () => {
    const changes: Array<{ rowId: string; patch: any }> = [];
    console.log("buildChangedCustomersPayload draftRows", draftRows);
    console.log("buildChangedCustomersPayload originalByCustomerNumber", originalByCustomerNumber);

    for (const row of draftRows) {
      const num = getCostumerNumberFromRow(row);
      console.log("buildChangedCustomersPayload num", num);
      if (!num) continue;

      const original = originalByCustomerNumber.get(num);
      console.log("buildChangedCustomersPayload original", original);
      if (!original) continue;

      const patch: any = { costumerNumber: num };
      console.log("buildChangedCustomersPayload patch", patch);
      let changed = false;

      for (const f of EDITABLE_FIELDS) {
        const newVal = getCustomerField(row, f);
        const oldVal = getCustomerField(original, f);
        if (newVal !== oldVal) {
          patch[f] = newVal;
          changed = true;
        }
      }

      if (changed) changes.push({ rowId: String(row.id), patch });
    }

    return changes;
  };

  const handleSaveClick = async () => {
    try {
      const changed = buildChangedCustomersPayload();
      console.log("changed", changed);

      if (changed.length === 0) {
        setIsEditing(false);
        setDraftRows([]);
        setOriginalRows([]);
        setPhoneErrorsByRowId({});
        return;
      }

      const errs: Record<string, string> = {};
      for (const item of changed) {
        if ("phoneNumber" in item.patch) {
          const msg = phoneErrorText(item.patch.phoneNumber);
          if (msg) errs[item.rowId] = msg;
        }
      }

      if (Object.keys(errs).length > 0) {
        setPhoneErrorsByRowId(errs);
        return;
      }

      await updateCostumersData(changed.map(x => x.patch));

      setIsEditing(false);
      setDraftRows([]);
      setOriginalRows([]);
      setPhoneErrorsByRowId({});

      await concertStore.fetchTicketsByEventPaged(
        event!.eventId,
        page,
        pageSize,
        filter.search,
        filter.sendStatus,
        missingOnly
      );
    } catch (error) {
      console.error("Failed to save changes", error);
    }
  };

  const handleChooseChannel = async (channel: TicketMessageChannel) => {
    try {
      // ===== BULK MODE =====
      if (bulkMode) {
        const rows = selectedRows;

        const orderCheck = validateSameOrderIdSales(rows);
        if (!orderCheck.ok) {
          setDialogPhoneError(orderCheck.message);
          return;
        }

        // (optional) validate phones for all
        for (const r of rows) {
          const phone = String(r?.customer?.phoneNumber ?? r?.phoneNumber ?? "");
          const msg = phoneErrorText(phone);
          if (msg) {
            setDialogPhoneError(msg);
            return;
          }
        }

        const barcodes = rows.map(r => String(r?.barcode ?? "").trim()).filter(Boolean);
        if (barcodes.length === 0) {
          setDialogPhoneError("Selected rows are missing barcodes");
          return;
        }

        await concertStore.sendInforUByChannelAndActionBulk(barcodes, channel, messageDialogAction);

        closeMessageDialog();
        setSelectedIds(new Set());
        handleFilter();
        return;
      }

      // ===== SINGLE MODE (send as array as requested) =====
      if (!messageDialogRow) return;

      const phone = String(
        messageDialogRow?.customer?.phoneNumber ?? messageDialogRow?.phoneNumber ?? ""
      );
      const msg = phoneErrorText(phone);

      if (msg) {
        setDialogPhoneError(msg);
        const rowId = String(messageDialogRow?.id ?? messageDialogRow?._id ?? "");
        if (isEditing && rowId) {
          setPhoneErrorsByRowId(prev => ({ ...prev, [rowId]: msg }));
        }
        return;
      }

      setDialogPhoneError("");

      const barcode = String(messageDialogRow?.barcode ?? "").trim();
      if (!barcode) {
        setDialogPhoneError("Missing barcode");
        return;
      }

      // ✅ ALWAYS ARRAY
      await concertStore.sendInforUByChannelAndActionBulk([barcode], channel, messageDialogAction);

      closeMessageDialog();
      handleFilter();
    } catch (e) {
      console.error("message send failed", e);
    }
  };

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "row-reverse" }}>
      <div style={{ margin: "30px 50px 0 50px", display: "flex", flexDirection: "column", flexGrow: 1 }}>
        <div className="concert-header-container">
          <div className="concert-header-title">
            <div className="concert-table-name" style={{ textAlign: "right" }}>
              {t(navBarStore.selectedTab.label)}
            </div>
          </div>

          <div className="concert-search-filter-container">
            <Box display="flex" flexDirection="row-reverse" alignItems="center" gap="20px" sx={{ width: "100%" }}>
              <Box sx={{ width: "100%", maxWidth: "260px", flexGrow: 1 }}>
                <Autocomplete
                  size="small"
                  style={{ width: "100%" }}
                  options={events}
                  getOptionLabel={(option: any) => option.eventName}
                  disableClearable
                  value={event || null}
                  isOptionEqualToValue={(option, value) => option.eventId === value.eventId}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={t("navbar.event")}
                      InputProps={{
                        ...params.InputProps,
                        inputProps: { ...params.inputProps, style: { textAlign: "right" } },
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        )
                      }}
                    />
                  )}
                  onChange={(ev: any, newValue: any) => {
                    handleCancelClick();
                    setEvent(newValue)
                  }
                  }
                />
              </Box>

              <Button
                size="small"
                variant="outlined"
                startIcon={<ScheduleIcon />}
                onClick={openDatePopover}
                sx={{ height: "39px", whiteSpace: "nowrap" }}
              >
                בחירת תאריכים
              </Button>

              {/* ✅ NEW: bulk send button (minimal UI change) */}
              <Button
                size="small"
                variant="contained"
                color="success"
                disabled={isEditing || selectedIds.size === 0}
                onClick={openBulkMessageDialog}
                sx={{ height: "39px", whiteSpace: "nowrap" }}
              >
                שלח נבחרו ({selectedIds.size})
              </Button>

              <IconButton
                size="small"
                onClick={handleRefresh}
                disabled={
                  !event?.eventId ||
                  concertStore.isLoading ||
                  refreshCooldown > 0
                }
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  height: "39px",
                  width: "39px",
                  ml: 1
                }}
                title={
                  refreshCooldown > 0
                    ? `Refresh available in ${refreshCooldown}s`
                    : "Refresh data"
                }
              >
                {concertStore.isLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
              </IconButton>
            </Box>

            {event && (
              <Typography variant="body1">
                {event.eventName} - {new Date(event.eventDate).toLocaleString("he-IL", {
                  timeZone: "UTC",
                  day: "numeric",
                  month: "numeric",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })}
                <span style={{ padding: "0 10px" }}>{" | "}</span>
                {event.eventLocation}
                <span style={{ padding: "0 10px" }}>{" | "}</span>
                {"סה״כ כרטיסים: " + concertStore.totalRecordsInDB}
              </Typography>
            )}

            <Box display="flex" flexDirection="row-reverse" width="100%" gap="20px" justifyContent="space-between" alignItems="center">

              <Box>
                <TextField
                  size="small"
                  sx={{ flex: 1 }}
                  placeholder={t("navbar.search")}
                  value={filter.search}
                  onKeyDown={onSearchFieldKeyDown}
                  onChange={e => {
                    handleCancelClick();
                    setFilter(prev => ({ ...prev, search: e.target.value }));
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleFilter(debouncedSearch)}
                          sx={{ minWidth: "36px", padding: "2px 8px" }}
                        >
                          {t("navbar.search")}
                        </Button>
                      </InputAdornment>
                    ),
                    inputProps: { style: { textAlign: "right" } }
                  }}
                />
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <ToggleButtonGroup
                  size="small"
                  value={filter.sendStatus}
                  onChange={(event, newStatuses) => setSendStatus(newStatuses || [])}
                >
                  {/* <ToggleButton value={SendStatus.PENDING}>{t("Pending")}</ToggleButton> */}
                  <ToggleButton value={SendStatus.SENT}>{t("Sent")}</ToggleButton>
                  <ToggleButton value={SendStatus.DELIVERED}>{t("Delivered")}</ToggleButton>
                  <ToggleButton value={SendStatus.READ}>{t("Read")}</ToggleButton>
                  <ToggleButton value={SendStatus.FAILED}>{t("Failed")}</ToggleButton>
                  <ToggleButton value={SendStatus.ELSE}>{t("Else")}</ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="body2">:{t("concertTickets.sendStatus")}</Typography>

              </Box>

              <Button
                size="small"
                variant={missingOnly ? "contained" : "outlined"}
                color="warning"
                disabled={isEditing || !event?.eventId}
                onClick={() => {
                  handleCancelClick();
                  setPage(0);
                  setMissingOnly(prev => !prev);
                }}
                sx={{ height: "35px", whiteSpace: "nowrap" }}
              >
                פרטים חסרים
              </Button>

              <Button size="small" variant="outlined" onClick={clearFilters} sx={{ height: "35px", whiteSpace: "nowrap" }}>
                {t("concertTickets.clearAll")}
              </Button>

              {!isEditing ? (
                <IconButton
                  size="small"
                  onClick={handleEditClick}
                  sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, height: "32px", width: "32px" }}
                  disabled={!mappedRows.length}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              ) : (
                <>
                  <Button size="small" variant="contained" onClick={handleSaveClick} sx={{ height: "35px", whiteSpace: "nowrap" }}>
                    {t("concertTickets.save")}
                  </Button>
                  <Button size="small" variant="outlined" onClick={handleCancelClick} sx={{ height: "35px", whiteSpace: "nowrap" }}>
                    {t("concertTickets.cancel")}
                  </Button>
                </>
              )}
            </Box>
          </div>
        </div>

        {concertStore.isLoading || isApplyingDateRange ? (
          <Box
            sx={{
              minHeight: 500,           // adjust to your table height
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <DataTable
            key={isEditing ? "editing" : "view"}
            rows={rowsToRender}
            columns={columns}
            pageSize={pageSize}
            setPageSize={setPageSize}
            page={page}
            setPage={setPage}
            totalRecordsInDB={concertStore.totalRecordsInDB}
            totalFiltered={concertStore.totalFilteredRecords}
          />
        )}

      </div>

      <DateRangePopover
        open={isDatePopoverOpen}
        anchorEl={dateAnchorEl}
        onClose={closeDatePopover}
        value={dateRange}
        onChange={setDateRange}
        onApply={applyDateRange}
      />

      <TicketMessageDialog
        open={messageDialogOpen}
        action={messageDialogAction}
        ticketRow={messageDialogRow}
        isEditing={isEditing}
        onClose={closeMessageDialog}
        onChoose={handleChooseChannel}
        phoneError={dialogPhoneError}
        bulkMode={bulkMode} // ✅ NEW
      />
    </div>
  );
});

export default ConcertTickets;